import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Col, Form, Image, InputGroup, Row } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import * as yup from "yup";
import type { FirmsController } from "../../../api/src/firms/firms.controller";
import type {
  CustodianCode,
  EditableFirm,
  Firm,
  ModelAssignmentStrategy,
  TradeOrderType,
} from "../../../api/src/firms/firms.service";
import type { SerializedObject, UnpackResponse } from "../../../api/src/lib";
import Loading from "../Loading";
import { NotificationContext } from "../Notifications";
import ActionButton from "../components/ActionButton";
import ConditionToggle from "../components/ConditionToggle";
import FormError from "../components/FormError";
import FormFieldError from "../components/FormFieldError";
import ImagePicker from "../components/ImagePicker";
import MaskedInput from "../components/MaskedInput";
import SubmitButton from "../components/SubmitButton";
import TabContainerWithTabs from "../components/TabContainer";
import {
  deserializeDate,
  useAuthenticatedFetch,
  useAuthenticatedMutationAsync,
} from "../lib/api";
import { useRoles } from "../lib/auth";
import { InlineError } from "../lib/display";
import {
  cashConditionSchema,
  emailSchema,
  getSchemaFieldLabel,
  idOptionalSelectorSchema,
  phoneNumberSchema,
} from "../lib/forms";
import { phoneMask } from "../lib/masks";
import AdvisorCodesEditor, { advisorCodeSchema } from "./AdvisorCodeEditor";
import FirmNav from "./FirmNav";
import UsersManager from "./UsersManager";
import { useQueryFeeStructures } from "../billing/lib";
import { activeSchwabAdvisorCodes } from "./lib";

const schema: yup.ObjectSchema<EditableFirm> = yup.object({
  name: yup.string().required().label("Firm Name"),
  // These three columns are nullable, and `Firm` now carries them as `| null`,
  // so the value `reset(firm)` puts in the form is an explicit null whenever
  // the column is NULL. Without `.nullable()` the resolver rejects it and the
  // save never fires. `.defined()` alongside it because the field is REQUIRED
  // on `EditableFirm`: the schema accepts null but still insists the key is
  // present. Same form as `AdvisorCodeEditor.tsx:51`, which APP-1525 wrote for
  // `closeDate`.
  phoneNumber: phoneNumberSchema.nullable().defined(),
  supportEmail: emailSchema.nullable().defined(),
  disclosures: yup.string().label("Disclosures"),
  primaryColor: yup.string().label("Primary Color"),
  accentColor: yup.string().label("Accent Color"),
  advisorCodes: advisorCodeSchema,
  // `idOptionalSelectorSchema` (`lib/forms.ts:198-206`) maps an empty selection
  // to `undefined`, which dropped the key from the request body altogether.
  // `?? null` turns that into an explicit null so the body matches the field's
  // declared shape. `PUT /firm/current` writes a fixed column list with `?? null`
  // on every value (`firms.service.ts:795-797`), so an explicit null and an
  // absent key reach the same column value either way.
  defaultFeeStructureId: idOptionalSelectorSchema
    .transform((value) => value ?? null)
    .nullable()
    .defined()
    .label("Default Fee Structure"),
  tlhThresholdAbsolute: yup
    .number()
    .min(0)
    .required()
    .label("Tax Loss Harvesting Threshold"),
  tlhThresholdPercent: yup
    .number()
    .min(0)
    .max(100)
    .required()
    .label("Tax Loss Harvesting Threshold – Percent"),
  tlhThresholdCondition: cashConditionSchema.label(
    "Tax Loss Harvesting Condition",
  ),
  tradeOrderType: yup
    .string()
    .required()
    .oneOf<TradeOrderType>(["market", "limit"])
    .label("Trade Order Type"),
  toleranceRiskPercent: yup
    .number()
    .min(0)
    .max(100)
    .required()
    .label("Risk Tolerance"),
  toleranceAssetClassPercent: yup
    .number()
    .min(0)
    .max(100)
    .required()
    .label("Asset Class Tolerance"),
  toleranceSecurityPercent: yup
    .number()
    .min(0)
    .max(100)
    .required()
    .label("Security Tolerance"),
  lotSelectionMethod: yup.string().optional().label("Lot Selection Method"),
  // These three were `.optional()`, which relaxes `undefined` only. yup 1.x is
  // non-nullable by default, so once the response carries an explicit null for
  // an unset rate — the ordinary case for a firm that has never entered one —
  // a bare `yup.number()` rejects it and the whole firm save stops. This pair
  // of sites is NOT in the job body's guard table; it was found by the
  // typecheck. `.defined()` for the same reason as `phoneNumber` above.
  stGainTaxRate: yup
    .number()
    .nullable()
    .defined()
    .min(0)
    .max(100)
    .label("ST Capital Gains Rate"),
  ltGainTaxRate: yup
    .number()
    .nullable()
    .defined()
    .min(0)
    .max(100)
    .label("LT Capital Gains Rate"),
  taxBudget: yup.number().nullable().defined().min(0).label("Tax Budget"),
  modelAssignmentStrategy: yup
    .string()
    .required()
    .oneOf<ModelAssignmentStrategy>(["fallback", "preference", "completion"])
    .label("Model Assignment Strategy"),
});

const FirmInfo = () => {
  const roles = useRoles();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    control,
    watch,
  } = useForm<EditableFirm>({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  const watchedLotSelectionMethod = watch("lotSelectionMethod");

  const {
    fields: advisorCodes,
    append: appendAdvisorCode,
    remove: removeAdvisorCode,
    replace: replaceAdvisorCode,
  } = useFieldArray({
    name: "advisorCodes",
    control,
    keyName: "_field_id",
  });

  const activeSchwabAccounts = useMemo(
    () => activeSchwabAdvisorCodes(advisorCodes),
    [advisorCodes],
  );

  const selectedBlockTradingCode = useMemo(
    () => advisorCodes.find((ac) => ac.blockTrading)?.code ?? "",
    [advisorCodes],
  );

  const onBlockTradingAccountChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedCode = e.target.value;
      replaceAdvisorCode(
        advisorCodes.map((ac) => ({
          ...ac,
          blockTrading: ac.code === selectedCode,
        })),
      );
    },
    [advisorCodes, replaceAdvisorCode],
  );

  const uploadRef = useRef<HTMLInputElement | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    isPending: isPendingFirm,
    isError: isErrorFirm,
    data: dataFirm,
  } = useAuthenticatedFetch<
    SerializedObject<UnpackResponse<FirmsController["getCurrentFirm"]>>
  >("/firm/current");
  const firm: Firm | undefined = useMemo(
    () =>
      typeof dataFirm?.data === "undefined"
        ? undefined
        : {
            ...dataFirm.data,
            advisorCodes: dataFirm.data.advisorCodes.map((advisorCode) => ({
              ...advisorCode,
              closeDate: deserializeDate(advisorCode.closeDate),
            })),
          },
    [dataFirm?.data],
  );

  useEffect(() => {
    if (typeof firm !== "undefined" && firm !== null) {
      reset(firm);
    }
  }, [firm, reset]);

  const {
    isPending: isPendingFeeStructures,
    isError: isErrorFeeStructures,
    data: dataFeeStructures,
  } = useQueryFeeStructures();

  const updateFirm = useAuthenticatedMutationAsync<
    UnpackResponse<FirmsController["updateCurrent"]>
  >("/firm/current", async (data: EditableFirm) => ({
    method: "PUT",
    body: JSON.stringify(data),
  }));

  const notificationContext = useContext(NotificationContext);

  const onSubmit = useMutation({
    mutationFn: async (data: EditableFirm) => {
      try {
        const body = await updateFirm(data);
        reset({
          ...body.data,
          advisorCodes: body.data.advisorCodes.map((ac) => ({
            ...ac,
            closeDate: deserializeDate(ac.closeDate),
          })),
        });
        notificationContext.pushNotification({
          id: `firm-${firm?.id}`,
          header: "Firm Updated",
          body: "Firm settings updated",
          variant: "success",
        });
      } catch (ex) {
        console.error("Failed to save firm details", ex);
        notificationContext.pushNotification({
          id: `firm-${firm?.id}`,
          header: "Failed to Update Firm",
          body: "Firm settings were not saved",
          variant: "danger",
        });
      }
    },
  });

  const { data: dataLogo, isPending: isPendingLogo } =
    useAuthenticatedFetch<UnpackResponse<FirmsController["getFirmLogo"]>>(
      "/firm/logo",
    );

  const uploadLogo = useAuthenticatedMutationAsync<
    UnpackResponse<FirmsController["uploadLogo"]>
  >("/firm/logo-upload", async (logo: Blob | null) => {
    const formData = new FormData();
    formData.append("logo", logo === null ? "" : logo);
    return {
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
      },
      body: formData,
    };
  });

  const queryClient = useQueryClient();

  const updateLogo = useCallback(
    (updatedLogo: any) => {
      queryClient.setQueryData(["/firm/logo"], {
        data: updatedLogo,
      });
    },
    [queryClient],
  );

  const handleSelectImage = useCallback(() => {
    if (roles.includes("Admin") && uploadRef !== null)
      uploadRef.current?.click();
  }, [uploadRef, roles]);

  const handleDeleteLogo = useCallback(async () => {
    if (roles.includes("Admin") && !isPendingLogo && dataLogo?.data) {
      setIsDeleting(true);
      try {
        await uploadLogo(undefined);
        updateLogo(undefined);
      } catch (err) {
        const message = "Failed to delete logo";
        console.error(message, err);
      }
      setIsDeleting(false);
    }
  }, [roles, isPendingLogo, dataLogo?.data, updateLogo, uploadLogo]);

  const onRetireAdvisorCode = useCallback(
    (code: string, custodian: CustodianCode, closeDate: Date) => {
      queryClient.setQueryData(["/firm/current"], {
        data: {
          ...firm,
          advisorCodes: firm?.advisorCodes.map((advisorCode) =>
            advisorCode.code !== code || advisorCode.custodian !== custodian
              ? advisorCode
              : { ...advisorCode, closeDate: closeDate.toISOString() },
          ),
        },
      });
    },
    [firm, queryClient],
  );

  return (
    <TabContainerWithTabs tabs={FirmNav}>
      {isPendingFirm || isPendingFeeStructures ? (
        <Loading />
      ) : isErrorFirm ? (
        <FormError message="Failed to load firm" />
      ) : typeof firm === "undefined" ? (
        <FormError message="Firm not found" />
      ) : (
        <Form onSubmit={handleSubmit((data) => onSubmit.mutateAsync(data))}>
          <fieldset disabled={!roles.includes("Admin")}>
            <Row>
              <Col lg={6} xs={12} className="mb-3 position-relative">
                <Row>
                  <Col xxl={8} lg={12} sm={8}>
                    {!roles.includes("Admin") &&
                    (isPendingLogo || !dataLogo?.data) ? null : (
                      <>
                        <Form.Label>Logo</Form.Label>
                        <div className="p-2 border rounded mb-3 position-relative">
                          {roles.includes("Admin") ? (
                            <ImagePicker
                              uploadFunc={uploadLogo}
                              uploadRef={uploadRef}
                              afterUpload={updateLogo}
                            />
                          ) : null}

                          <Image
                            src={
                              !isPendingLogo && dataLogo?.data
                                ? `data:${dataLogo?.data?.mimetype};base64,${dataLogo?.data?.image}`
                                : "/replace.png"
                            }
                            width="100%"
                            role={roles.includes("Admin") ? "button" : "img"}
                            onClick={handleSelectImage}
                          />

                          {roles.includes("Admin") &&
                          !isPendingLogo &&
                          dataLogo?.data ? (
                            <ActionButton
                              className="position-absolute top-13 end-13"
                              variant="icon"
                              icon="/icons/trash.svg"
                              label="Remove"
                              type="button"
                              onClick={handleDeleteLogo}
                              disabled={isDeleting}
                            />
                          ) : null}
                        </div>
                      </>
                    )}
                  </Col>
                  <Col xs={12}>
                    <Form.Group className="mb-3" controlId="form-name">
                      <Form.Label>
                        {getSchemaFieldLabel(schema.fields.name)}
                      </Form.Label>
                      <Form.Control type="text" {...register("name")} />
                      <FormFieldError field={errors.name} />
                    </Form.Group>
                  </Col>
                  <Col xxl={6}>
                    <Form.Group className="mb-3" controlId="form-supportEmail">
                      <Form.Label>
                        {getSchemaFieldLabel(schema.fields.supportEmail)}
                      </Form.Label>
                      <Form.Control type="text" {...register("supportEmail")} />
                      <FormFieldError field={errors.supportEmail} />
                    </Form.Group>
                  </Col>
                  <Col xxl={6}>
                    <Form.Group className="mb-3" controlId="form-phoneNumber">
                      <Form.Label>
                        {getSchemaFieldLabel(schema.fields.phoneNumber)}
                      </Form.Label>
                      <MaskedInput
                        name="phoneNumber"
                        type="tel"
                        mask={phoneMask.mask}
                        placeholder="+x (xxx)-xxx-xxxx"
                        control={control}
                      />
                      <FormFieldError field={errors.phoneNumber} />
                    </Form.Group>
                  </Col>
                  {!roles.includes("Admin") ? null : (
                    <Col xxl={6} className="mb-3">
                      <Form.Label>Billing</Form.Label>
                      <br />
                      <a
                        href={`${
                          process.env.REACT_APP_BILLING_URL
                        }?prefilled_email=${firm.supportEmail ?? ""}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View Billing Information
                      </a>
                    </Col>
                  )}
                  <Col xs={12}>
                    <Form.Group className="mb-3" controlId="form-disclosures">
                      <Form.Label>
                        {getSchemaFieldLabel(schema.fields.disclosures)}
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={5}
                        {...register("disclosures")}
                      />
                      <FormFieldError field={errors.disclosures} />
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <Form.Group
                      className="mb-3"
                      controlId="form-defaultFeeStructure"
                    >
                      <Form.Label>
                        {getSchemaFieldLabel(
                          schema.fields.defaultFeeStructureId,
                        )}
                      </Form.Label>
                      {isErrorFeeStructures ? (
                        <InlineError>Failed to load fee structures</InlineError>
                      ) : (
                        <Form.Select {...register("defaultFeeStructureId")}>
                          <option value="">None</option>
                          {(dataFeeStructures?.data ?? []).map(
                            (feeStructure) => (
                              <option
                                key={feeStructure.feeStructureId}
                                value={feeStructure.feeStructureId}
                              >
                                {feeStructure.name}
                              </option>
                            ),
                          )}
                        </Form.Select>
                      )}
                      <FormFieldError field={errors.defaultFeeStructureId} />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col xs={12}>
                    <h3>Trade Proposals</h3>
                  </Col>
                </Row>

                <Row>
                  <Col xs={12}>
                    <h5 className="mt-3">Tolerance Defaults</h5>
                  </Col>
                  <Col xxl={4} xl={6} lg={12} md={4} sm={6}>
                    <Form.Group
                      className="mb-3"
                      controlId="form-toleranceRiskPercent"
                    >
                      <Form.Label>
                        {getSchemaFieldLabel(
                          schema.fields.toleranceRiskPercent,
                        )}
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          min={0}
                          max={100}
                          step={0.0001}
                          {...register("toleranceRiskPercent")}
                        />
                        <InputGroup.Text>%</InputGroup.Text>
                      </InputGroup>
                      <FormFieldError field={errors.toleranceRiskPercent} />
                    </Form.Group>
                  </Col>
                  <Col xxl={4} xl={6} lg={12} md={4} sm={6}>
                    <Form.Group
                      className="mb-3"
                      controlId="form-toleranceAssetClassPercent"
                    >
                      <Form.Label>
                        {getSchemaFieldLabel(
                          schema.fields.toleranceAssetClassPercent,
                        )}
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          min={0}
                          max={100}
                          step={0.0001}
                          {...register("toleranceAssetClassPercent")}
                        />
                        <InputGroup.Text>%</InputGroup.Text>
                      </InputGroup>
                      <FormFieldError
                        field={errors.toleranceAssetClassPercent}
                      />
                    </Form.Group>
                  </Col>
                  <Col xxl={4} xl={6} lg={12} md={4} sm={6}>
                    <Form.Group
                      className="mb-3"
                      controlId="form-toleranceSecurityPercent"
                    >
                      <Form.Label>
                        {getSchemaFieldLabel(
                          schema.fields.toleranceSecurityPercent,
                        )}
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          min={0}
                          max={100}
                          step={0.0001}
                          {...register("toleranceSecurityPercent")}
                        />
                        <InputGroup.Text>%</InputGroup.Text>
                      </InputGroup>
                      <FormFieldError field={errors.toleranceSecurityPercent} />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col xs={12}>
                    <h5 className="mt-3">Lot Selection Method</h5>
                  </Col>
                  <Col lg={6}>
                    <Form.Group
                      className="mb-3"
                      controlId="form-lotSelectionMethod"
                    >
                      <Form.Select {...register("lotSelectionMethod")}>
                        <option value="">-- No default method --</option>
                        <option value="HIFO">
                          HIFO (Highest In, First Out)
                        </option>
                        <option value="MTAX">MTAX (Minimum Tax)</option>
                      </Form.Select>
                      <FormFieldError field={errors.lotSelectionMethod} />
                    </Form.Group>
                  </Col>
                </Row>

                {watchedLotSelectionMethod === "MTAX" && (
                  <Row className="mb-3">
                    <Col lg={4}>
                      <Form.Group controlId="form-stGainTaxRate">
                        <Form.Label>
                          {getSchemaFieldLabel(schema.fields.stGainTaxRate)}
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="number"
                            step={0.0001}
                            min={0}
                            max={100}
                            {...register("stGainTaxRate")}
                          />
                          <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup>
                        <FormFieldError field={errors.stGainTaxRate} />
                      </Form.Group>
                    </Col>
                    <Col lg={4}>
                      <Form.Group controlId="form-ltGainTaxRate">
                        <Form.Label>
                          {getSchemaFieldLabel(schema.fields.ltGainTaxRate)}
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="number"
                            step={0.0001}
                            min={0}
                            max={100}
                            {...register("ltGainTaxRate")}
                          />
                          <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup>
                        <FormFieldError field={errors.ltGainTaxRate} />
                      </Form.Group>
                    </Col>
                    <Col lg={4}>
                      <Form.Group controlId="form-taxBudget">
                        <Form.Label>
                          {getSchemaFieldLabel(schema.fields.taxBudget)}
                        </Form.Label>
                        <InputGroup>
                          <InputGroup.Text>$</InputGroup.Text>
                          <Form.Control
                            type="number"
                            min={0}
                            step={0.01}
                            {...register("taxBudget")}
                          />
                        </InputGroup>
                        <FormFieldError field={errors.taxBudget} />
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                <Row>
                  <Col xs={12} className="mt-4">
                    <Form.Label>
                      {getSchemaFieldLabel(schema.fields.tlhThresholdAbsolute)}
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text>$</InputGroup.Text>
                      <Form.Control
                        type="number"
                        min={0}
                        step={0.01}
                        {...register("tlhThresholdAbsolute")}
                      />
                      <ConditionToggle
                        name="tlhThresholdCondition"
                        control={control}
                      />
                      <Form.Control
                        type="number"
                        min={0}
                        max={100}
                        step={0.0001}
                        {...register("tlhThresholdPercent")}
                      />
                      <InputGroup.Text>%</InputGroup.Text>
                    </InputGroup>
                    <FormFieldError field={errors.tlhThresholdCondition} />
                    <FormFieldError field={errors.tlhThresholdAbsolute} />
                    <FormFieldError field={errors.tlhThresholdPercent} />
                  </Col>
                </Row>

                <Row>
                  <Col xs={12}>
                    <Form.Group
                      className="mb-3 mt-4"
                      controlId="form-tradeOrderType"
                    >
                      <Form.Label>
                        {getSchemaFieldLabel(schema.fields.tradeOrderType)}
                      </Form.Label>
                      <Form.Select {...register("tradeOrderType")}>
                        <option value="market">Market</option>
                        <option value="limit">Limit</option>
                      </Form.Select>
                      <FormFieldError field={errors.tradeOrderType} />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col xs={12}>
                    <Form.Group
                      className="mb-3 mt-4"
                      controlId="form-modelAssignmentStrategy"
                    >
                      <Form.Label>
                        {getSchemaFieldLabel(
                          schema.fields.modelAssignmentStrategy,
                        )}
                      </Form.Label>
                      <Form.Select {...register("modelAssignmentStrategy")}>
                        <option value="fallback">
                          Fallback - Inherit from household model
                        </option>
                        <option value="preference">
                          Preference - Rules-based assignment
                        </option>
                        <option value="completion">
                          Completion - Optimize for household target
                        </option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Determines how accounts without a directly assigned
                        model receive their target allocations.
                      </Form.Text>
                      <FormFieldError field={errors.modelAssignmentStrategy} />
                    </Form.Group>
                  </Col>
                </Row>
              </Col>
              <Col lg={6} xs={12}>
                {roles.includes("Admin") ? <UsersManager /> : null}
                <Col>
                  <h3>Master Accounts</h3>
                  <AdvisorCodesEditor
                    fields={advisorCodes}
                    replace={replaceAdvisorCode}
                    add={appendAdvisorCode}
                    remove={removeAdvisorCode}
                    register={register}
                    errors={errors.advisorCodes}
                    onRetire={onRetireAdvisorCode}
                  />
                </Col>
                {activeSchwabAccounts.length > 0 && (
                  <Col>
                    <Form.Group
                      className="mb-3 mt-4"
                      controlId="form-schwabBlockTradingAccount"
                    >
                      <Form.Label>Schwab Block Trading Account</Form.Label>
                      <Form.Select
                        value={selectedBlockTradingCode}
                        onChange={onBlockTradingAccountChange}
                      >
                        <option value="">None</option>
                        {activeSchwabAccounts.map((ac) => (
                          <option key={ac.code} value={ac.code}>
                            {ac.accountNumber}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Master account used for executing Schwab block trades
                      </Form.Text>
                    </Form.Group>
                  </Col>
                )}
              </Col>
            </Row>
            <Row>
              <Col>
                {roles.includes("Admin") ? (
                  <SubmitButton isSubmitting={isSubmitting} />
                ) : null}
              </Col>
            </Row>
          </fieldset>
        </Form>
      )}
    </TabContainerWithTabs>
  );
};

export default FirmInfo;
